import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { catalogueApi } from '../lib/api';
import { Header } from '../components/Header';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import CatalogueNumberInput from '../components/CatalogueNumberInput';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader,
    DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
    Plus, Search, Upload, Pencil, Trash2, Library, Disc, FileText,
    AlertCircle, LayoutGrid, List, Package, ShoppingCart, AlertTriangle, RefreshCw, Plug,
    Download, Link2, LinkIcon,
    ArrowUpRight, ArrowDownRight, ArrowRight
} from 'lucide-react';

/**
 * LazyImage — drop-in <img> replacement that:
 *  • Defers network fetch with `loading="lazy"` (browser only loads when the
 *    image scrolls into the viewport)
 *  • Shows a pulsing slate skeleton until the first paint completes so the
 *    grid/table renders instantly with no layout shift
 *  • Falls back to an optional `fallback` node (the initials block) if the
 *    cover URL errors out (e.g. expired presigned S3 URL between renders)
 */
const LazyImage = ({ src, alt = '', className = '', fallback = null, testId }) => {
    const [loaded, setLoaded] = useState(false);
    const [failed, setFailed] = useState(false);
    // Reset when src changes (e.g. presigned URL refresh)
    useEffect(() => {
        setLoaded(false);
        setFailed(false);
    }, [src]);
    if (failed && fallback) return fallback;
    return (
        <div className={`relative ${className}`}>
            {!loaded && (
                <div className="absolute inset-0 bg-slate-100 animate-pulse" aria-hidden="true" />
            )}
            <img
                src={src}
                alt={alt}
                loading="lazy"
                decoding="async"
                onLoad={() => setLoaded(true)}
                onError={() => setFailed(true)}
                className={`w-full h-full object-cover transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                data-testid={testId}
            />
        </div>
    );
};

// Color palette for title initials
const INITIAL_COLORS = [    'bg-violet-500', 'bg-blue-500', 'bg-emerald-500', 'bg-amber-500',
    'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-teal-500',
    'bg-orange-500', 'bg-pink-500', 'bg-sky-500', 'bg-lime-500',
];

const getInitialColor = (str) => {
    let hash = 0;
    for (let i = 0; i < (str || '').length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return INITIAL_COLORS[Math.abs(hash) % INITIAL_COLORS.length];
};

const getInitials = (title, artist) => {
    if (title) return title.substring(0, 2).toUpperCase();
    if (artist) return artist.substring(0, 2).toUpperCase();
    return '??';
};

const getStockHealth = (entry) => {
    if (entry.stock_quantity === null || entry.stock_quantity === undefined) return null;
    const threshold = entry.low_stock_threshold || 50;
    const qty = entry.stock_quantity;
    if (qty <= 0) return { level: 'critical', color: 'bg-red-500', text: 'Out of stock', textColor: 'text-red-600' };
    if (qty <= threshold * 0.5) return { level: 'critical', color: 'bg-red-500', text: 'Critical', textColor: 'text-red-600' };
    if (qty <= threshold) return { level: 'low', color: 'bg-amber-400', text: 'Low stock', textColor: 'text-amber-600' };
    return { level: 'good', color: 'bg-emerald-500', text: 'In stock', textColor: 'text-emerald-600' };
};

const getStockPercent = (entry) => {
    if (entry.stock_quantity === null || entry.stock_quantity === undefined) return 0;
    const threshold = entry.low_stock_threshold || 50;
    const maxRef = threshold * 3;
    return Math.min(100, (entry.stock_quantity / maxRef) * 100);
};

const formatMediaType = (entry) => {
    const mt = (entry.media_type || '').toLowerCase();
    if (mt === 'cd') return 'CD';
    if (mt === 'dvd') return 'DVD';
    if (mt === 'blu_ray') return 'Blu-ray';
    if (mt === 'vinyl' && entry.format) return `${entry.format}" Vinyl`;
    if (mt === 'vinyl') return 'Vinyl';
    return null;
};

const formatRunway = (days) => {
    if (days === null || days === undefined) return null;
    if (days <= 0) return 'Out of stock';
    if (days < 7) return `~${Math.round(days)} day${Math.round(days) !== 1 ? 's' : ''} left`;
    if (days < 30) return `~${Math.round(days / 7)} week${Math.round(days / 7) !== 1 ? 's' : ''} left`;
    if (days < 365) return `~${Math.round(days / 30)} month${Math.round(days / 30) !== 1 ? 's' : ''} left`;
    return `~${Math.round(days / 365)} year${Math.round(days / 365) !== 1 ? 's' : ''} left`;
};

// ─── Inventory Intelligence (Overview compact mode) ───
// Deliberately minimal for the catalogue overview: just a trend chip
// and an optional "Reorder recommended" badge. All the detailed
// forecast numbers (velocity, run-out date, suggested qty, confidence)
// live on the Title Details page so the overview stays scannable at
// hundreds+ of catalogue titles.
const TREND_META = {
    up:      { label: 'Increasing',  color: 'text-emerald-700', icon: ArrowUpRight,   bg: 'bg-emerald-50 border-emerald-100' },
    down:    { label: 'Decreasing',  color: 'text-rose-700',    icon: ArrowDownRight, bg: 'bg-rose-50 border-rose-100' },
    stable:  { label: 'Stable',      color: 'text-slate-600',   icon: ArrowRight,     bg: 'bg-slate-50 border-slate-100' },
    no_data: null, // no chip when there's no signal at all
};

const InventoryIntelligence = ({ forecast }) => {
    if (!forecast) return null;
    const trend = TREND_META[forecast.trend];
    const showReorder = !!forecast.reorder_recommended;
    if (!trend && !showReorder) return null;
    const TrendIcon = trend?.icon;
    return (
        <div
            className="mt-2 flex items-center gap-1.5 flex-wrap"
            data-testid="inventory-intelligence-panel"
        >
            {trend && (
                <span
                    className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${trend.color} ${trend.bg}`}
                    title="Demand trend vs previous 30 days"
                >
                    <TrendIcon className="h-3 w-3" />
                    {trend.label}
                </span>
            )}
            {showReorder && (
                <span
                    className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                    title="Based on current sales pace + production lead time — open the title for full details"
                    data-testid="reorder-recommended-badge"
                >
                    <AlertCircle className="h-3 w-3" />
                    Reorder recommended
                </span>
            )}
        </div>
    );
};

// ─── Stock Health Bar ───
const StockHealthBar = ({ entry }) => {
    const health = getStockHealth(entry);
    if (!health) return <span className="text-[11px] text-slate-400">No stock tracking</span>;
    const pct = getStockPercent(entry);
    const runway = formatRunway(entry.stock_runway_days);
    const hasVelocity = entry.stock_velocity_30d > 0;

    // Combine status + runway for the right-side label
    const statusWithRunway = runway
        ? `${health.text} · ${runway}`
        : health.text;

    return (
        <div className="mt-2">
            <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${health.color}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="flex items-center justify-between mt-1">
                <span className={`text-[11px] font-medium ${health.textColor}`}>
                    {entry.stock_quantity.toLocaleString()} units
                    {!hasVelocity && entry.stock_quantity > 0 && entry.order_count > 0 && (
                        <span className="text-slate-400 font-normal"> · Slow moving</span>
                    )}
                    {!hasVelocity && entry.order_count === 0 && entry.stock_quantity > 0 && (
                        <span className="text-slate-400 font-normal"> · No usage data</span>
                    )}
                </span>
                <span className={`text-[10px] ${health.textColor}`}>{statusWithRunway}</span>
            </div>
        </div>
    );
};

// ─── Title Card ───
const TitleCard = ({ entry, onEdit, onDelete, onClick }) => {
    const initials = getInitials(entry.title, entry.artist);
    const bgColor = getInitialColor(entry.catalogue_number);
    const media = formatMediaType(entry);

    return (
        <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow group cursor-pointer" 
            data-testid={`catalogue-card-${entry.id}`}
            onClick={onClick}>
            {/* Art / Cover image */}
            {entry.cover_image ? (
                <LazyImage
                    src={entry.cover_image}
                    alt={entry.title || entry.catalogue_number}
                    className="rounded-lg h-32 overflow-hidden mb-3"
                    fallback={
                        <div className={`${bgColor} rounded-lg h-32 flex items-center justify-center mb-3`}>
                            <span className="text-3xl font-bold text-white/90">{initials}</span>
                        </div>
                    }
                />
            ) : (
                <div className={`${bgColor} rounded-lg h-32 flex items-center justify-center mb-3`}>
                    <span className="text-3xl font-bold text-white/90">{initials}</span>
                </div>
            )}

            {/* Info */}
            <div className="min-h-[60px]">
                <p className="text-[10px] font-mono text-slate-400 mb-0.5">{entry.catalogue_number}</p>
                <h3 className="text-sm font-semibold text-slate-900 leading-tight truncate" title={entry.title}>
                    {entry.title || entry.catalogue_number}
                </h3>
                <p className="text-xs text-slate-500 truncate mt-0.5">{entry.artist || '—'}</p>
            </div>

            {/* Meta row */}
            <div className="flex items-center gap-2 mt-2">
                {media && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                        {media}
                    </span>
                )}
                {entry.pricing_linked ? (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-600" data-testid="pricing-linked-badge">
                        <Link2 className="h-2.5 w-2.5" /> Pricing linked
                    </span>
                ) : entry.sku ? (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-600" data-testid="pricing-unlinked-badge">
                        No pricing
                    </span>
                ) : null}
                {entry.order_count > 0 && (
                    <span className="text-[10px] text-slate-400">{entry.order_count} order{entry.order_count !== 1 ? 's' : ''}</span>
                )}
            </div>

            {/* Stock bar */}
            <StockHealthBar entry={entry} />

            {/* Inventory Intelligence (Version 1) forecast */}
            <InventoryIntelligence forecast={entry.forecast} />

            {/* Auto reorder indicator */}
            {entry.auto_reorder_enabled && (
                <div className="flex items-center gap-1 mt-1.5">
                    <RefreshCw className="h-3 w-3 text-violet-500" />
                    <span className="text-[10px] text-violet-500 font-medium">Auto reorder: {entry.auto_reorder_quantity?.toLocaleString() || '—'} units</span>
                </div>
            )}

            {/* Actions (visible on hover) */}
            <div className="flex gap-1 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="ghost" size="sm" className="h-7 text-xs flex-1" onClick={(e) => { e.stopPropagation(); onEdit(entry); }}>
                    <Pencil className="h-3 w-3 mr-1" /> Edit
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); onDelete(entry); }}>
                    <Trash2 className="h-3 w-3" />
                </Button>
            </div>
        </div>
    );
};

// ─── Add Title Card ───
const AddTitleCard = ({ onClick }) => (
    <button
        onClick={onClick}
        className="rounded-xl border-2 border-dashed border-slate-200 hover:border-slate-400 p-4 flex flex-col items-center justify-center min-h-[280px] transition-colors group"
        data-testid="add-title-card"
    >
        <div className="w-12 h-12 rounded-full bg-slate-100 group-hover:bg-slate-200 flex items-center justify-center transition-colors mb-3">
            <Plus className="h-5 w-5 text-slate-400 group-hover:text-slate-600" />
        </div>
        <span className="text-sm font-medium text-slate-500 group-hover:text-slate-700">Add title</span>
    </button>
);

// ─── Main Page ───
export default function CataloguePage() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [entries, setEntries] = useState([]);
    const [metrics, setMetrics] = useState(null);
    const [aiInsights, setAiInsights] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState('card');
    const [activeFilter, setActiveFilter] = useState('all');

    // Dialog states
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);

    const [formData, setFormData] = useState({
        catalogue_number: '', title: '', artist: '', format: '',
        media_type: '', stock_quantity: '', low_stock_threshold: '50',
        auto_reorder_enabled: false, auto_reorder_quantity: '',
        min_reorder_quantity: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importResult, setImportResult] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (searchQuery) params.search = searchQuery;
            if (activeFilter === 'vinyl' || activeFilter === 'cd' || activeFilter === 'dvd' || activeFilter === 'blu_ray') params.media_type = activeFilter;
            if (activeFilter === 'low_stock') params.low_stock = true;

            const [entriesRes, metricsRes] = await Promise.all([
                catalogueApi.getAll(params),
                catalogueApi.getMetrics(),
            ]);
            setEntries(entriesRes.data);
            setMetrics(metricsRes.data);
        } catch (error) {
            toast.error('Failed to load catalogue');
        } finally {
            setLoading(false);
        }
    }, [searchQuery, activeFilter]);

    useEffect(() => {
        const timeout = setTimeout(fetchData, 300);
        return () => clearTimeout(timeout);
    }, [fetchData]);

    // Fetch AI insights separately (cached, not affected by search)
    useEffect(() => {
        catalogueApi.getAiInsights()
            .then(res => setAiInsights(res.data))
            .catch(() => {});
    }, []);

    const handleAdd = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await catalogueApi.create({
                catalogue_number: formData.catalogue_number,
                title: formData.title || null,
                artist: formData.artist || null,
                format: formData.format || null,
                media_type: formData.media_type || null,
                stock_quantity: formData.stock_quantity !== '' ? parseInt(formData.stock_quantity) : null,
                low_stock_threshold: formData.low_stock_threshold ? parseInt(formData.low_stock_threshold) : 50,
                auto_reorder_enabled: formData.auto_reorder_enabled,
                auto_reorder_quantity: formData.auto_reorder_quantity ? parseInt(formData.auto_reorder_quantity) : null,
                min_reorder_quantity: formData.min_reorder_quantity ? parseInt(formData.min_reorder_quantity) : null,
            });
            toast.success('Title added to catalogue');
            setIsAddDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.status === 409 ? 'Catalogue number already exists' : 'Failed to create entry');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        if (!selectedEntry) return;
        setIsSubmitting(true);
        try {
            await catalogueApi.update(selectedEntry.id, {
                catalogue_number: formData.catalogue_number,
                title: formData.title || null,
                artist: formData.artist || null,
                format: formData.format || null,
                media_type: formData.media_type || null,
                stock_quantity: formData.stock_quantity !== '' ? parseInt(formData.stock_quantity) : null,
                low_stock_threshold: formData.low_stock_threshold ? parseInt(formData.low_stock_threshold) : 50,
                auto_reorder_enabled: formData.auto_reorder_enabled,
                auto_reorder_quantity: formData.auto_reorder_quantity ? parseInt(formData.auto_reorder_quantity) : null,
                min_reorder_quantity: formData.min_reorder_quantity ? parseInt(formData.min_reorder_quantity) : null,
            });
            toast.success('Title updated');
            setIsEditDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.status === 409 ? 'Catalogue number already exists' : 'Failed to update');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedEntry) return;
        try {
            await catalogueApi.delete(selectedEntry.id);
            toast.success('Title removed');
            setIsDeleteDialogOpen(false);
            setSelectedEntry(null);
            fetchData();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const handleImport = async () => {
        if (!importFile) return;
        setIsSubmitting(true);
        try {
            const res = await catalogueApi.import(importFile);
            setImportResult(res.data);
            toast.success(`Import complete: ${res.data.created} created, ${res.data.updated} updated`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Import failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    const openEditDialog = (entry) => {
        setSelectedEntry(entry);
        setFormData({
            catalogue_number: entry.catalogue_number,
            title: entry.title || '',
            artist: entry.artist || '',
            format: entry.format || '',
            media_type: entry.media_type || '',
            stock_quantity: entry.stock_quantity !== null && entry.stock_quantity !== undefined ? String(entry.stock_quantity) : '',
            low_stock_threshold: String(entry.low_stock_threshold || 50),
            auto_reorder_enabled: entry.auto_reorder_enabled || false,
            auto_reorder_quantity: entry.auto_reorder_quantity ? String(entry.auto_reorder_quantity) : '',
            min_reorder_quantity: entry.min_reorder_quantity ? String(entry.min_reorder_quantity) : '',
        });
        setIsEditDialogOpen(true);
    };

    const resetForm = () => {
        setFormData({ catalogue_number: '', title: '', artist: '', format: '', media_type: '', stock_quantity: '', low_stock_threshold: '50', auto_reorder_enabled: false, auto_reorder_quantity: '', min_reorder_quantity: '' });
        setSelectedEntry(null);
    };

    const resetImport = () => { setImportFile(null); setImportResult(null); };

    const FILTERS = [
        { key: 'all', label: t('catalogue.filter.all', 'All') },
        { key: 'vinyl', label: t('catalogue.filter.vinyl', 'Vinyl') },
        { key: 'cd', label: t('catalogue.filter.cd', 'CD') },
        { key: 'dvd', label: t('catalogue.filter.dvd', 'DVD') },
        { key: 'blu_ray', label: t('catalogue.filter.blu_ray', 'Blu-ray') },
        { key: 'low_stock', label: t('catalogue.filter.low_stock', 'Low stock') },
    ];

    // ─── Entry Form (shared between Add and Edit) ───
    const EntryFormFields = () => (
        <>
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                    <CatalogueNumberInput
                        label="Catalogue Number"
                        value={formData.catalogue_number}
                        onChange={(v) => setFormData({ ...formData, catalogue_number: v })}
                        placeholder="e.g., CAT-001"
                        required
                        data-testid="catalogue-number-input"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs">Media Type</Label>
                    <Select value={formData.media_type} onValueChange={(v) => setFormData({ ...formData, media_type: v })}>
                        <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="vinyl">Vinyl</SelectItem>
                            <SelectItem value="cd">CD</SelectItem>
                            <SelectItem value="dvd">DVD</SelectItem>
                            <SelectItem value="blu_ray">Blu-ray</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="space-y-1.5">
                <Label className="text-xs">Title</Label>
                <Input placeholder="Album / Release title" value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
                <Label className="text-xs">Artist</Label>
                <Input placeholder="Artist name" value={formData.artist}
                    onChange={(e) => setFormData({ ...formData, artist: e.target.value })} />
            </div>
            {formData.media_type === 'vinyl' && (
                <div className="space-y-1.5">
                    <Label className="text-xs">Vinyl Format</Label>
                    <Select value={formData.format} onValueChange={(v) => setFormData({ ...formData, format: v })}>
                        <SelectTrigger><SelectValue placeholder="Select size" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="7">7"</SelectItem>
                            <SelectItem value="10">10"</SelectItem>
                            <SelectItem value="12">12"</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            )}
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                    <Label className="text-xs">Stock Quantity</Label>
                    <Input type="number" min="0" placeholder="Units" value={formData.stock_quantity}
                        onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs">Low Stock Threshold</Label>
                    <Input type="number" min="0" placeholder="50" value={formData.low_stock_threshold}
                        onChange={(e) => setFormData({ ...formData, low_stock_threshold: e.target.value })} />
                </div>
            </div>
            {/* Auto Reorder */}
            <div className="border border-slate-200 rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <RefreshCw className="h-3.5 w-3.5 text-slate-500" />
                        <Label className="text-xs font-medium">Auto Reorder</Label>
                    </div>
                    <Switch checked={formData.auto_reorder_enabled}
                        onCheckedChange={(v) => setFormData({ ...formData, auto_reorder_enabled: v })}
                        data-testid="auto-reorder-toggle" />
                </div>
                {formData.auto_reorder_enabled && (
                    <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">Reorder Quantity</Label>
                        <Input type="number" min="1" placeholder="e.g., 500" value={formData.auto_reorder_quantity}
                            onChange={(e) => setFormData({ ...formData, auto_reorder_quantity: e.target.value })}
                            data-testid="auto-reorder-quantity" />
                    </div>
                )}
                <div className="space-y-1.5 pt-1">
                    <Label className="text-xs text-slate-500">
                        Minimum reorder quantity <span className="text-slate-400">(optional override)</span>
                    </Label>
                    <Input
                        type="number"
                        min="1"
                        placeholder="Leave empty to use family default"
                        value={formData.min_reorder_quantity}
                        onChange={(e) => setFormData({ ...formData, min_reorder_quantity: e.target.value })}
                        data-testid="min-reorder-quantity"
                    />
                    <p className="text-[11px] text-slate-500">
                        Overrides the product-family default (Vinyl / CD / DVD / Blu-ray)
                        for this specific catalogue entry only.
                    </p>
                </div>
            </div>
        </>
    );

    if (loading && entries.length === 0) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="flex items-center justify-center h-[calc(100vh-4rem)]"><LoadingSpinner size="lg" /></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="catalogue-page">
            <Header />
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

                {/* ─── Page Header ─── */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
                            {t('catalogue.title', 'Catalogue library')}
                        </h1>
                        <p className="text-sm text-slate-500 mt-0.5">
                            {user?.active_company_name || t('catalogue.your_company', 'Your company')} &middot; {metrics?.total_titles || entries.length} {t('catalogue.titles_suffix', 'titles')}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* View toggle */}
                        <div className="flex bg-slate-100 rounded-lg p-0.5" data-testid="view-toggle">
                            <button onClick={() => setViewMode('card')}
                                className={`p-1.5 rounded-md transition ${viewMode === 'card' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}>
                                <LayoutGrid className="h-4 w-4" />
                            </button>
                            <button onClick={() => setViewMode('list')}
                                className={`p-1.5 rounded-md transition ${viewMode === 'list' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}>
                                <List className="h-4 w-4" />
                            </button>
                        </div>

                        {/* Integrations */}
                        <Button variant="outline" size="sm" onClick={() => navigate('/catalogue/integrations')} data-testid="catalogue-integrations-btn">
                            <Plug className="h-3.5 w-3.5 mr-1.5" /> {t('catalogue.integrations_btn', 'Integrations')}
                        </Button>

                        {/* Import CSV */}
                        <Dialog open={isImportDialogOpen} onOpenChange={(open) => { setIsImportDialogOpen(open); if (!open) resetImport(); }}>
                            <DialogTrigger asChild>
                                <Button variant="outline" size="sm" data-testid="import-catalogue-btn">
                                    <Upload className="h-3.5 w-3.5 mr-1.5" /> {t('catalogue.import_btn', 'Import')}
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-md">
                                <DialogHeader>
                                    <DialogTitle>{t('catalogue.import.title', 'Import Catalogue Entries')}</DialogTitle>
                                    <DialogDescription>{t('catalogue.import.description', 'Upload a CSV with columns: catalogue_number, title, artist, media_type, stock_quantity, low_stock_threshold, sku')}</DialogDescription>
                                </DialogHeader>
                                {!importResult ? (
                                    <div className="space-y-4 py-4">
                                        <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                                            <input type="file" accept=".csv,.xlsx" onChange={(e) => setImportFile(e.target.files?.[0] || null)} className="hidden" id="csv-upload" />
                                            <label htmlFor="csv-upload" className="cursor-pointer">
                                                <FileText className="h-10 w-10 mx-auto mb-3 text-slate-400" />
                                                {importFile ? <p className="text-sm font-medium">{importFile.name}</p> : <p className="text-sm text-slate-500">{t('catalogue.import.pick_file', 'Click to select a CSV or Excel file')}</p>}
                                            </label>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    const res = await catalogueApi.downloadImportTemplate();
                                                    const url = window.URL.createObjectURL(new Blob([res.data]));
                                                    const a = document.createElement('a');
                                                    a.href = url;
                                                    a.download = 'catalogue_import_template.xlsx';
                                                    a.click();
                                                    window.URL.revokeObjectURL(url);
                                                } catch { toast.error(t('catalogue.import.template_fail', 'Failed to download template')); }
                                            }}
                                            className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 transition-colors"
                                            data-testid="download-csv-template-btn"
                                        >
                                            <Download className="h-3.5 w-3.5" /> {t('catalogue.import.template_btn', 'Download Excel template')}
                                        </button>
                                        <DialogFooter>
                                            <Button variant="outline" onClick={() => setIsImportDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
                                            <Button onClick={handleImport} disabled={!importFile || isSubmitting} data-testid="import-submit-btn">{isSubmitting ? t('catalogue.import.importing', 'Importing...') : t('catalogue.import_btn', 'Import')}</Button>
                                        </DialogFooter>
                                    </div>
                                ) : (
                                    <div className="space-y-4 py-4">
                                        <div className="grid grid-cols-3 gap-4 text-center">
                                            <div className="p-3 bg-emerald-50 rounded-lg"><p className="text-2xl font-bold text-emerald-600" data-testid="import-created">{importResult.created}</p><p className="text-xs text-slate-600">{t('catalogue.import.created', 'Created')}</p></div>
                                            <div className="p-3 bg-blue-50 rounded-lg"><p className="text-2xl font-bold text-blue-600" data-testid="import-updated">{importResult.updated}</p><p className="text-xs text-slate-600">{t('catalogue.import.updated', 'Updated')}</p></div>
                                            <div className="p-3 bg-amber-50 rounded-lg"><p className="text-2xl font-bold text-amber-600" data-testid="import-skipped">{importResult.skipped}</p><p className="text-xs text-slate-600">{t('catalogue.import.skipped', 'Skipped')}</p></div>
                                        </div>
                                        {importResult.warnings?.length > 0 && (
                                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="import-warnings">
                                                <p className="text-sm font-medium text-amber-700 mb-2 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> SKU Warnings ({importResult.warnings.length})</p>
                                                <ul className="text-xs text-amber-600 space-y-1 max-h-32 overflow-y-auto">
                                                    {importResult.warnings.map((w, i) => (
                                                        <li key={i}>Row {w.row}: {w.catalogue_number} — {w.message}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {importResult.errors?.length > 0 && (
                                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                                <p className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1"><AlertCircle className="h-4 w-4" /> Errors ({importResult.errors.length})</p>
                                                <ul className="text-xs text-red-600 space-y-1 max-h-32 overflow-y-auto">{importResult.errors.map((err, i) => <li key={i}>{typeof err === 'string' ? err : err.message}</li>)}</ul>
                                            </div>
                                        )}
                                        <DialogFooter><Button onClick={() => { setIsImportDialogOpen(false); resetImport(); }}>Done</Button></DialogFooter>
                                    </div>
                                )}
                            </DialogContent>
                        </Dialog>

                        {/* Add title */}
                        <Dialog open={isAddDialogOpen} onOpenChange={(open) => { setIsAddDialogOpen(open); if (!open) resetForm(); }}>
                            <DialogTrigger asChild>
                                <Button size="sm" className="bg-slate-900 hover:bg-slate-800" data-testid="add-catalogue-btn">
                                    <Plus className="h-3.5 w-3.5 mr-1.5" /> {t('catalogue.add_title_btn', 'Add title')}
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-md">
                                <DialogHeader>
                                    <DialogTitle>{t('catalogue.add.title', 'Add Title')}</DialogTitle>
                                    <DialogDescription>{t('catalogue.add.description', 'Add a new title to your catalogue library.')}</DialogDescription>
                                </DialogHeader>
                                <form onSubmit={handleAdd} className="space-y-3 py-3">
                                    <EntryFormFields />
                                    <DialogFooter>
                                        <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
                                        <Button type="submit" disabled={isSubmitting}>{isSubmitting ? t('catalogue.add.adding', 'Adding...') : t('catalogue.add.title', 'Add Title')}</Button>
                                    </DialogFooter>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>

                {/* ─── Metrics ─── */}
                {metrics && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="metric-titles">
                            <div className="flex items-center gap-2 text-slate-500 mb-1"><Library className="h-4 w-4" /><span className="text-xs">Titles in catalogue</span></div>
                            <p className="text-2xl font-bold text-slate-900">{metrics.total_titles}</p>
                        </div>
                        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="metric-stock">
                            <div className="flex items-center gap-2 text-slate-500 mb-1"><Package className="h-4 w-4" /><span className="text-xs">Units in stock</span></div>
                            <p className="text-2xl font-bold text-slate-900">{metrics.total_stock.toLocaleString()}</p>
                        </div>
                        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="metric-orders">
                            <div className="flex items-center gap-2 text-slate-500 mb-1"><ShoppingCart className="h-4 w-4" /><span className="text-xs">Active orders</span></div>
                            <p className="text-2xl font-bold text-slate-900">{metrics.active_orders}</p>
                        </div>
                        <div className={`bg-white rounded-xl border p-4 ${metrics.low_stock_count > 0 ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200'}`} data-testid="metric-low-stock">
                            <div className="flex items-center gap-2 text-slate-500 mb-1"><AlertTriangle className={`h-4 w-4 ${metrics.low_stock_count > 0 ? 'text-amber-500' : ''}`} /><span className="text-xs">Low stock</span></div>
                            <p className={`text-2xl font-bold ${metrics.low_stock_count > 0 ? 'text-amber-600' : 'text-slate-900'}`}>{metrics.low_stock_count}</p>
                        </div>
                    </div>
                )}

                {/* ─── AI Insights Banner ─── */}
                {aiInsights?.message && (
                    <div className="flex items-start gap-3 bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200/60 rounded-xl p-4 mb-6" data-testid="ai-insights-banner">
                        <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <AlertTriangle className="h-4 w-4 text-violet-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-700 leading-relaxed">{aiInsights.message}</p>
                            {aiInsights.titles?.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {aiInsights.titles.map(t => (
                                        <button key={t.catalogue_number}
                                            onClick={() => {
                                                const match = entries.find(e => e.catalogue_number === t.catalogue_number);
                                                if (match) navigate(`/catalogue/${match.id}`);
                                            }}
                                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700 hover:bg-violet-200 transition">
                                            {t.title || t.catalogue_number}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ─── Search + Filters ─── */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input placeholder="Search by catalogue #, title, or artist..." value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 h-9 text-sm" data-testid="catalogue-search" />
                    </div>
                    <div className="flex gap-1">
                        {FILTERS.map(f => (
                            <button key={f.key} onClick={() => setActiveFilter(f.key)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                                    activeFilter === f.key
                                        ? 'bg-slate-900 text-white'
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`} data-testid={`filter-${f.key}`}>
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ─── Content ─── */}
                {entries.length === 0 && !loading ? (
                    <div className="py-16 text-center">
                        <Library className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-slate-900 mb-1">{searchQuery || activeFilter !== 'all' ? 'No titles found' : 'No titles yet'}</h3>
                        <p className="text-sm text-slate-500 mb-4">{searchQuery ? 'Try a different search term' : 'Add your first title or import from CSV'}</p>
                        {!searchQuery && activeFilter === 'all' && (
                            <Button onClick={() => setIsAddDialogOpen(true)}><Plus className="h-4 w-4 mr-2" /> Add title</Button>
                        )}
                    </div>
                ) : viewMode === 'card' ? (
                    /* ─── Card Grid ─── */
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {entries.map(entry => (
                            <TitleCard key={entry.id} entry={entry}
                                onClick={() => navigate(`/catalogue/${entry.id}`)}
                                onEdit={openEditDialog}
                                onDelete={(e) => { setSelectedEntry(e); setIsDeleteDialogOpen(true); }}
                            />
                        ))}
                        <AddTitleCard onClick={() => setIsAddDialogOpen(true)} />
                    </div>
                ) : (
                    /* ─── List View ─── */
                    <Card className="border border-slate-200">
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-background hover:bg-background">
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500">Title</TableHead>
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500">Artist</TableHead>
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500 w-20">Cat. #</TableHead>
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500 w-24">Format</TableHead>
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500 w-32">Stock</TableHead>
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500 w-24">Pricing</TableHead>
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500 w-16">Orders</TableHead>
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500 w-20">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {entries.map(entry => {
                                        const health = getStockHealth(entry);
                                        return (
                                            <TableRow key={entry.id} className="h-12 cursor-pointer hover:bg-slate-50" 
                                                data-testid={`catalogue-row-${entry.id}`}
                                                onClick={() => navigate(`/catalogue/${entry.id}`)}>
                                                <TableCell className="font-medium text-slate-900 text-sm">
                                                    <div className="flex items-center gap-3">
                                                        {entry.cover_image ? (
                                                            <LazyImage
                                                                src={entry.cover_image}
                                                                alt=""
                                                                className="w-10 h-10 rounded border border-slate-200 flex-shrink-0 overflow-hidden"
                                                                fallback={
                                                                    <div className="w-10 h-10 rounded bg-slate-100 border border-slate-200 flex-shrink-0 flex items-center justify-center text-[9px] font-mono text-slate-400">
                                                                        {entry.catalogue_number?.slice(0, 4) || '—'}
                                                                    </div>
                                                                }
                                                                testId={`catalogue-list-cover-${entry.id}`}
                                                            />
                                                        ) : (
                                                            <div className="w-10 h-10 rounded bg-slate-100 border border-slate-200 flex-shrink-0 flex items-center justify-center text-[9px] font-mono text-slate-400">
                                                                {entry.catalogue_number?.slice(0, 4) || '—'}
                                                            </div>
                                                        )}
                                                        <span className="truncate">{entry.title || '—'}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-slate-600 text-sm">{entry.artist || '—'}</TableCell>
                                                <TableCell className="font-mono text-xs text-slate-500">{entry.catalogue_number}</TableCell>
                                                <TableCell>
                                                    {formatMediaType(entry) ? (
                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">{formatMediaType(entry)}</span>
                                                    ) : <span className="text-slate-400">—</span>}
                                                </TableCell>
                                                <TableCell>
                                                    {health ? (
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-16 h-1 bg-slate-100 rounded-full overflow-hidden">
                                                                <div className={`h-full rounded-full ${health.color}`} style={{ width: `${getStockPercent(entry)}%` }} />
                                                            </div>
                                                            <span className={`text-xs font-medium ${health.textColor}`}>{entry.stock_quantity}</span>
                                                        </div>
                                                    ) : <span className="text-xs text-slate-400">—</span>}
                                                </TableCell>
                                                <TableCell>
                                                    {entry.pricing_linked ? (
                                                        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600" data-testid="list-pricing-linked">
                                                            <Link2 className="h-3 w-3" /> Linked
                                                        </span>
                                                    ) : (
                                                        <span className="text-[10px] text-slate-400">No pricing</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-xs text-slate-500">{entry.order_count || 0}</TableCell>
                                                <TableCell>
                                                    <div className="flex gap-1">
                                                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditDialog(entry)}><Pencil className="h-3.5 w-3.5" /></Button>
                                                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                            onClick={() => { setSelectedEntry(entry); setIsDeleteDialogOpen(true); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                )}

                {entries.length > 0 && (
                    <p className="text-xs text-slate-500 mt-3">Showing {entries.length} title{entries.length !== 1 ? 's' : ''}</p>
                )}
            </main>

            {/* ─── Edit Dialog ─── */}
            <Dialog open={isEditDialogOpen} onOpenChange={(open) => { setIsEditDialogOpen(open); if (!open) resetForm(); }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Edit Title</DialogTitle>
                        <DialogDescription>Update details for this catalogue entry.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleEdit} className="space-y-3 py-3">
                        <EntryFormFields />
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Save Changes'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* ─── Delete Confirmation ─── */}
            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete title?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently remove "{selectedEntry?.title || selectedEntry?.catalogue_number}" from your catalogue.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
